import { Grid } from '@material-ui/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-final-form';
import _ from 'lodash';

import { fetchTributosTtceByNcmPais } from '../../../features/ncm-subitem/ncmSubitemAPI';

import QCXSelectManyFundamentosOpcionais from './components/QCXSelectManyFundamentosOpcionais';
import QCXSelectManyFundamentosObrigatorios from './components/QCXSelectManyFundamentosObrigatorios';
import QCXFormSubtitle from '../../form-title/QCXFormSubtitle';
import QCXAtributosTratamentoTributarioDuimpV2 from '../../../shared-components/atributos-tratamento-tributario-duimp/QCXAtributosTratamentoTributarioDuimpV2';

export default function TributosAdicaoDuimpForm({ values, disabled, idFatura }) {
  const form = useForm();

  const [idNcm, setIdNcm] = useState(null);
  const [idPais, setIdPais] = useState(null);
  const [tributosRaw, setTributosRaw] = useState([]);

  const [fundamentosObrigatorios, setFundamentosObrigatorios] = useState([]);
  const [fundamentosOpcionaisGrouped, setFundamentosOpcionaisGrouped] = useState([]);

  const obterIdPaisAdicao = (adicao) => {
    let pais = null;
    const relacao = _.get(adicao, 'relacao', null);
    const exportadorPais =
      _.get(adicao, 'exportador.endereco.cidade.estado.pais.id', null) ??
      _.get(adicao, 'exportador.endereco.estado.pais.id', null) ??
      _.get(adicao, 'exportador.endereco.pais.id', null);
    const fabricantePais =
      _.get(adicao, 'fabricante.endereco.cidade.estado.pais.id', null) ??
      _.get(adicao, 'fabricante.endereco.estado.pais.id', null) ??
      _.get(adicao, 'fabricante.endereco.pais.id', null);

    if (relacao === 'FABRICANTE_EXPORTADOR') {
      pais = exportadorPais;
    } else if (relacao === 'FABRICANTE_NAO_EXPORTADOR') {
      pais = fabricantePais;
    } else if (relacao === 'FABRICANTE_DESCONHECIDO') {
      pais = _.get(adicao, 'paisOrigem.id', null) ?? _.get(adicao, 'mercadorias[0].paisOrigem.id', null);
    }

    return pais;
  };

  useEffect(() => {
    const ncm = values?.ncm?.id;
    const pais = obterIdPaisAdicao(values);

    if (ncm !== idNcm) {
      setIdNcm(ncm);
    }

    if (pais !== idPais) {
      setIdPais(pais);
    }
  }, [values]);

  const agruparFundamentos = (fundamentosMetadadosArr) => {
    if (!fundamentosMetadadosArr) return [];

    const fundamentos = {};

    fundamentosMetadadosArr.forEach((item) => {
      const codigoFundamento = item.fundamentoLegal.codigo;
      const nomeFundamento = item.fundamentoLegal.nome;
      const tipoFundamento = item.fundamentoLegal.tipo;

      if (!fundamentos[codigoFundamento]) {
        fundamentos[codigoFundamento] = {
          id: codigoFundamento,
          nome: nomeFundamento,
          tipo: tipoFundamento,
          tratamentos: [],
        };
      }

      fundamentos[codigoFundamento].tratamentos.push({
        regime: {
          codigo: item?.regime?.codigo,
          nome: item?.regime?.nome,
        },
        tributo: {
          codigo: item?.tributo?.codigo,
          nome: item?.tributo?.nome,
        },
      });
    });

    const fundamentosParsed = Object.values(fundamentos).map((fundamento) => ({ ...fundamento }));
    const fundamentosOrdenados = fundamentosParsed.sort((a, b) => a.id.localeCompare(b.id));

    return fundamentosOrdenados;
  };

  useEffect(() => {
    if (idNcm && idPais) {
      fetchTributosTtceByNcmPais(idNcm, idPais)
        .then((response) => {
          const responseData = response?.data || {};
          setTributosRaw(responseData);
          const tratamentosObrigatorios = _.isArray(responseData?.tratamentosTributarios)
            ? responseData.tratamentosTributarios
            : [];
          const tratamentosOpcionaisDisponiveis = _.isArray(responseData?.fundamentosOpcionaisDisponiveis)
            ? responseData.fundamentosOpcionaisDisponiveis
            : [];

          if (tratamentosOpcionaisDisponiveis.length) {
            const fundamentosOrdenados = agruparFundamentos(tratamentosOpcionaisDisponiveis);
            setFundamentosOpcionaisGrouped(fundamentosOrdenados);
          }

          if (tratamentosObrigatorios.length) {
            // agrupa fundamentos obrigatorios para exibicao
            const fundamentosOrdenados = agruparFundamentos(tratamentosObrigatorios);
            setFundamentosObrigatorios(fundamentosOrdenados);
          }

          const valoresMetadadosSalvos = _.get(values, 'valoresMetadadoTtce.valorMetadadosJson', {});
          const valoresPorCodigo = {};
          const ordemAtributosPorTratamento = {};

          [...tratamentosObrigatorios, ...tratamentosOpcionaisDisponiveis].forEach((tratamento) => {
            const codigoTratamento = obterCodigoTratamento(tratamento);
            const codigosAtributos = [];

            (tratamento?.mercadorias || []).forEach((mercadoria) => {
              (mercadoria?.atributos || []).forEach((atributo) => {
                if (atributo?.codigo && !codigosAtributos.includes(atributo.codigo)) {
                  codigosAtributos.push(atributo.codigo);
                }
              });
            });

            ordemAtributosPorTratamento[codigoTratamento] = codigosAtributos;
          });

          ['obrigatorios', 'opcionais'].forEach((grupo) => {
            Object.values(valoresMetadadosSalvos?.[grupo] || {}).forEach((tratamento) => {
              (tratamento?.atributos || []).forEach((atributo) => {
                valoresPorCodigo[atributo.codigo] = atributo.valor;
              });
            });
          });

          const atributosValores = {};
          [...tratamentosObrigatorios, ...tratamentosOpcionaisDisponiveis].forEach((tratamento) => {
            const codigoTratamento = obterCodigoTratamento(tratamento);

            (tratamento?.mercadorias || []).forEach((mercadoria) => {
              (mercadoria?.atributos || []).forEach((atributo) => {
                if (!atributo?.codigo) return;

                const atributoAtual = atributosValores[atributo.codigo];
                const codTratamentosPossiveis = atributoAtual?.codTratamentosPossiveis || [];

                atributosValores[atributo.codigo] = {
                  ...atributoAtual,
                  codigo: atributo.codigo,
                  valor: Object.prototype.hasOwnProperty.call(valoresPorCodigo, atributo.codigo)
                    ? valoresPorCodigo[atributo.codigo]
                    : atributoAtual?.valor || '',
                  codTratamentosPossiveis: [...new Set([...codTratamentosPossiveis, codigoTratamento])],
                };
              });
            });
          });

          form.change('ignorableFields.tratamentosTtce.metadados.valores', atributosValores);
          form.change(
            'ignorableFields.tratamentosTtce.metadados.ordemAtributosPorTratamento',
            ordemAtributosPorTratamento
          );

        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [idNcm, idPais]);

  const obterCodigoTratamento = useCallback((tratamento) => {
    const fundamentoCodigo =
      tratamento?.fundamentoLegal?.codigo ?? tratamento?.fundamento?.id ?? tratamento?.fundamento?.codigo;
    return `${tratamento?.tributo?.codigo};${tratamento?.regime?.codigo};${fundamentoCodigo}`;
  }, []);

  const tributosFiltradosRaw = useMemo(() => {
    const tratamentosObrigatorios = Array.isArray(tributosRaw?.tratamentosTributarios)
      ? tributosRaw.tratamentosTributarios
      : [];
    const tratamentosOpcionaisDisponiveis = Array.isArray(tributosRaw?.fundamentosOpcionaisDisponiveis)
      ? tributosRaw.fundamentosOpcionaisDisponiveis
      : [];
    const tratamentosObrigatoriosSelecionados = _.get(
      values,
      'ignorableFields.tratamentosTtce.metadados.obrigatorios',
      []
    );
    const tratamentosOpcionaisSelecionados = _.get(
      values,
      'ignorableFields.tratamentosTtce.metadados.opcionais',
      []
    );
    const codigosTratamentosSelecionados = new Set(
      [...tratamentosObrigatoriosSelecionados, ...tratamentosOpcionaisSelecionados].map(obterCodigoTratamento)
    );

    return [...tratamentosObrigatorios, ...tratamentosOpcionaisDisponiveis].filter((tratamento) =>
      codigosTratamentosSelecionados.has(obterCodigoTratamento(tratamento))
    );
  }, [tributosRaw, values, obterCodigoTratamento]);

  return (
    <>
      <Grid item xs={12} sm={12} md={12} lg={12}>
        <QCXFormSubtitle title="Tratamentos Opcionais" />
      </Grid>

      <QCXSelectManyFundamentosOpcionais
        title="Fundamentos Opcionais"
        name="ignorableFields.tratamentosTtce.metadados.opcionais"
        values={values}
        disabled={disabled}
        configureFilterPredicate={() => true}
        fundamentosOpcionaisGrouped={fundamentosOpcionaisGrouped}
        idFatura={idFatura}
      />

      <Grid item xs={12} sm={12} md={12} lg={12}>
        <QCXFormSubtitle title="Tratamentos Obrigatorios" />
      </Grid>

      <QCXSelectManyFundamentosObrigatorios
        title="Fundamentos Obrigatorios"
        name="ignorableFields.tratamentosTtce.metadados.obrigatorios"
        values={values}
        disabled={disabled}
        configureFilterPredicate={() => true}
        fundamentosGrouped={fundamentosObrigatorios}
      />

      <>
        <QCXAtributosTratamentoTributarioDuimpV2
          name="ignorableFields.tratamentosTtce.metadados.valores"
          tributos={tributosFiltradosRaw}
    isConsult={disabled}
        />
      </>

    </>
  );
}
